import { Alert } from '@creditornot/cb-alert';
import React, { useState } from 'react';
import styled from 'styled-components';

import { DocumentDropzone } from './DocumentDropzone';
import UploadedFile from './UploadedFile';
import { MAX_SIZE_BYTES, MAX_SIZE_MB, MB_BYTES } from './constants';

type DocumentUploaderProps = {
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onChange?: (file: File) => void;
  isUploading?: boolean;
  failedToUpload?: boolean;
  uploadedFiles: File[];
  onDeletedFile: (file: File) => void;
  fileBeingDeletedName?: string | null;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-end: 16px;
`;

const ErrorAlert = styled(Alert)`
  margin-block-start: 16px;
`;

export const DocumentUploader = ({
  inputProps,
  onChange,
  uploadedFiles,
  isUploading,
  failedToUpload,
  onDeletedFile,
  fileBeingDeletedName,
}: DocumentUploaderProps) => {
  const [fileTooLarge, setFileTooLarge] = useState(false);

  const hasError = failedToUpload || fileTooLarge;

  const handleSelectFile = (file: File) => {
    if (file.size > MAX_SIZE_BYTES) {
      setFileTooLarge(true);
      return;
    } else {
      setFileTooLarge(false);
    }

    if (onChange) {
      onChange(file);
    }
  };

  return (
    <Root>
      <DocumentDropzone
        hasError={hasError}
        onSelectFile={handleSelectFile}
        inputProps={inputProps}
        isLoading={isUploading}
      />

      {hasError && (
        <ErrorAlert title="File type not supported" variant="error">
          Please use JPG, PDF, SVG, or CSV, smaller than {MAX_SIZE_MB}MB.
        </ErrorAlert>
      )}

      {uploadedFiles.map((file, index) => (
        <UploadedFile
          key={`${file.name}-${index}`}
          name={file.name}
          size={Math.round((file.size / MB_BYTES) * 10) / 10}
          onDelete={() => onDeletedFile(file)}
          isDeleting={fileBeingDeletedName === file.name}
        />
      ))}
    </Root>
  );
};
